import styles from './styles.module.scss';

import { useContext, useEffect, useState } from 'react';
import { Board } from '../../../components/Board';
import { ProfileBox } from '../../../components/ProfileBox';
import {
    getReportFilterTypes,
    hasPermission,
    hasRoles,
    verifyRoutePermissions
} from '../../../globalFunctions';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import 'moment/locale/pt';
import { LoadData, School, SchoolClass, User } from '../../../types';
import { useReportOptions } from '../../../hooks/useReportOptions';
import { MultipleSelectionList } from '../../../components/MultipleSelectionList';
import { Loading } from '../../../components/Loading';
import { Report } from '../../../components/Report';
import { WindowSizeContext } from '../../../providers/WindowSizeProvider';
import { MessageContext } from '../../../providers/MessageProvider';
import { LoadingContext } from '../../../providers/LoadingProvider';
import { UsersRegistersReportFilters, UsersRegistersReportOptions } from './types';
import { Select } from '../../../components/Select';
import { ReportDAO } from '../../../classes/ReportDAO';
import { NavButton } from '../../../components/NavButton';
import { TextField } from '@mui/material';
import moment from 'moment';

export const LogicManagerReportRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const { setIsLoadingContent } = useContext(LoadingContext);
    const windowSize = useContext(WindowSizeContext);
    const navigate = useNavigate();

    const options: LoadData = useReportOptions('users.registers');
    const [loadedOptions, setLoadedOptions] = useState<UsersRegistersReportOptions>();
    const [schoolsSelected, setSchoolsSelected] = useState<number[]>([]);
    const [classesSelected, setClassesSelected] = useState<number[]>([]);
    const [usersSelected, setUsersSelected] = useState<number[]>([]);

    const [filterType, setFilterType] = useState<'users' | 'classes' | 'schools'>();
    const [fromDate, setFromDate] = useState<any>(null);
    const [toDate, setToDate] = useState<any>(null);
    const [reportData, setReportData] = useState<string>();
    const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);

    const onPrintReport = () => {
        if (reportData) {
            const iframe = document.getElementById('report') as HTMLIFrameElement;

            if (iframe?.contentWindow) {
                iframe.contentWindow.print();
            }
        } else {
            setMessage({
                type: 'info',
                content: 'Para imprimir o relatório, primeiro pressione o botão "Gerar relatório"'
            });
            setOpenMessage(true);
        }
    };

    const validate = async (request: UsersRegistersReportFilters) => {
        if (request.optionsId.length === 0) {
            setMessage({
                type: 'warning',
                content: 'Selecione pelo menos uma opção do filtro'
            });
            setOpenMessage(true);
            return false;
        }

        if (
            request.fromDate === null ||
            request.fromDate === '' ||
            request.fromDate === 'Invalid date'
        ) {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo de data inicial'
            });
            setOpenMessage(true);
            return false;
        }

        if (
            request.toDate === null ||
            request.toDate === '' ||
            request.toDate === 'Invalid date'
        ) {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo de data final'
            });
            setOpenMessage(true);
            return false;
        }

        // Converta as datas string para moment antes de usar diff
        const from = moment(request.fromDate, 'YYYY-MM-DD');
        const to = moment(request.toDate, 'YYYY-MM-DD');

        if (
            to.diff(from, 'months', true) > 1 ||
            from.diff(to, 'months', true) > 1
        ) {
            setMessage({
                type: 'warning',
                content: 'O limite de período para o relatório é de um mês'
            });
            setOpenMessage(true);
            return false;
        }

        return true;
    };

    const prepareReportFilters = () => {
        if (filterType) {
            const reportRequest: UsersRegistersReportFilters = {
                filterType: filterType,
                isAllOptions: false,
                optionsId: [],
                fromDate: fromDate,
                toDate: toDate
            };

            switch (filterType) {
                case 'users':
                    if (usersSelected.length === 0) {
                        loadedOptions?.users.forEach((user: User) => {
                            usersSelected.push(user.id);
                        });
                    }

                    if (loadedOptions?.users.length === usersSelected.length) {
                        reportRequest.isAllOptions = true;
                    }

                    reportRequest.optionsId = usersSelected;
                    break;

                case 'classes':
                    if (classesSelected.length === 0) {
                        loadedOptions?.classes.forEach((schoolClass: SchoolClass) => {
                            classesSelected.push(schoolClass.id);
                        });
                    }

                    if (loadedOptions?.classes.length === classesSelected.length) {
                        reportRequest.isAllOptions = true;
                    }

                    reportRequest.optionsId = classesSelected;
                    break;

                case 'schools':
                    if (schoolsSelected.length === 0) {
                        loadedOptions?.schools.forEach((school: School) => {
                            schoolsSelected.push(school.id);
                        });
                    }

                    if (loadedOptions?.schools.length === schoolsSelected.length) {
                        reportRequest.isAllOptions = true;
                    }

                    reportRequest.optionsId = schoolsSelected;
                    break;
            }

            return reportRequest ?? [];
        } else {
            setMessage({
                type: 'warning',
                content: 'Selecione um tipo de filtro'
            });
            setOpenMessage(true);
            setIsLoadingReport(false);

            return null;
        }
    };

    const onGenerateReport = async () => {
        try {
            if (windowSize.width <= 1200) {
                setIsLoadingContent(true);
                setMessage({
                    type: 'info',
                    content: 'Carregando dados do relatório'
                });
                setOpenMessage(true);
            } else {
                setIsLoadingReport(true);
            }

            const reportRequest = prepareReportFilters();

            if (!reportRequest) {
                return;
            }

            const isValidRequest = await validate(reportRequest);

            if (isValidRequest) {
                reportRequest.toDate = moment(reportRequest.toDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
                reportRequest.fromDate = moment(reportRequest.fromDate, 'YYYY-MM-DD').format('DD/MM/YYYY');

                const result = await ReportDAO.getData(userAuth.token, navigate, reportRequest);

                if (
                    result &&
                    typeof result === 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    if (result.report === undefined) {
                        setMessage({
                            type: 'error',
                            content: 'Erro ao gerar o relatório'
                        });
                        setOpenMessage(true);
                    } else if (result.report === '') {
                        setMessage({
                            type: 'info',
                            content: 'Sem dados'
                        });
                        setOpenMessage(true);
                    }

                    setReportData(result.report);
                } else {
                    throw result;
                }
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: error.message
            });
            setOpenMessage(true);
        } finally {
            if (windowSize.width <= 1200) {
                setIsLoadingContent(false);
            } else {
                setIsLoadingReport(false);
            }
        }
    };

    const goBackwards = () => {
        navigate(`/manager`);
    };

    useEffect(() => {
        if (!options.isLoading) {
            const data = options.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                setLoadedOptions({
                    users: data.users,
                    classes: data.classes,
                    schools: data.schools
                });
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [options.isLoading]);

    useEffect(() => {
        if (reportData && windowSize.width <= 1200) {
            onPrintReport();
        }
    }, [reportData]);

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
            setFilterType(hasRoles(['ADMIN'], user) ? 'users' : 'classes');
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />

            {userAuth.token && <ProfileBox user={userAuth} />}

            {userAuth.token && !options.isLoading && loadedOptions && (
                <>
                    <Board wrapperClass={styles.board}>
                        <div className={styles.content}>
                            <h1 className={styles.title}>Relatório</h1>

                            <div className={styles.container}>
                                <div className={styles.filters}>
                                    <div className={styles.group}>
                                        <h2 className={styles.groupLabel}>Período</h2>

                                        <div className={styles.betweenDates}>
                                            <LocalizationProvider
                                                dateAdapter={AdapterMoment}
                                                adapterLocale='pt'
                                            >
                                                <TextField
                                                    label="Data inicial"
                                                    type="date"
                                                    value={fromDate}
                                                    onChange={e => setFromDate(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                                <TextField
                                                    label="Data final"
                                                    type="date"
                                                    value={toDate}
                                                    onChange={e => setToDate(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />


                                            </LocalizationProvider>
                                        </div>
                                    </div>

                                    <div className={`${styles.group} ${styles.usersList}`}>
                                        <Select
                                            wrapperClass={styles.filterType}
                                            options={getReportFilterTypes(
                                                hasPermission('logicmanager.admin', userAuth)
                                            )}
                                            value={filterType}
                                            onChange={(e: any) => setFilterType(e.target.value)}
                                        />

                                        {filterType === 'users' && (
                                            <MultipleSelectionList
                                                data={loadedOptions.users}
                                                primaryValue={'name'}
                                                secondaryValue={'description'}
                                                setSelectedItems={setUsersSelected}
                                                selectedItems={usersSelected}
                                            />
                                        )}

                                        {filterType === 'classes' && (
                                            <MultipleSelectionList
                                                data={loadedOptions.classes}
                                                primaryValue={'name'}
                                                secondaryValue={'description'}
                                                setSelectedItems={setClassesSelected}
                                                selectedItems={classesSelected}
                                            />
                                        )}

                                        {filterType === 'schools' && (
                                            <MultipleSelectionList
                                                data={loadedOptions.schools}
                                                primaryValue={'name'}
                                                secondaryValue={'description'}
                                                setSelectedItems={setSchoolsSelected}
                                                selectedItems={schoolsSelected}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className={styles.reportData}>
                                    <div className={styles.buttonBar}>
                                        <Button
                                            onClick={onGenerateReport}
                                            variant='contained'
                                            color='success'
                                        >
                                            Gerar relatório
                                        </Button>
                                        <Button
                                            className={styles.printButton}
                                            onClick={onPrintReport}
                                            variant='outlined'
                                            color='success'
                                        >
                                            Imprimir
                                        </Button>
                                    </div>
                                    <div className={`${styles.group} ${styles.reportBox}`}>
                                        {isLoadingReport ? (
                                            <Loading />
                                        ) : reportData === undefined ? (
                                            <div className={styles.defaultMessage}>
                                                Preencha os campos para gerar o relatório
                                            </div>
                                        ) : reportData === '' ? (
                                            <div className={styles.defaultMessage}>Sem dados</div>
                                        ) : (
                                            <Report report={reportData} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Board>
                </>
            )}
        </div>
    );
};